import {
  afterNextRender,
  Component,
  ElementRef,
  inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, NavigationSkipped, Router } from '@angular/router';
import { distinctUntilChanged, filter, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppSection, PublicAppSection } from 'angular/app/core/store/ui/ui.state';
import { selectAppSection, selectAuthUser } from '../../core/store/reducers';
import { fromStore } from '../../core/store';
import { map } from 'rxjs/operators';
import { DestroyableComponent } from '../../shared/components/destroyable/destroyable.component';
import { isPlatformBrowser } from '@angular/common';
import { LocalizedUrlService } from '../../core/services/localized-url.service';

@Component({
  selector: 'app-navbar',
  styles: [
    `
      .toggler {
        label span {
          transition: 0.4s cubic-bezier(0.68, -0.6, 0.32, 1.6);
        }
        input[type='checkbox']:checked ~ span:nth-of-type(1) {
          transform-origin: bottom;
          transform: rotatez(45deg) translate(3px, 0px);
        }
        input[type='checkbox']:checked ~ span:nth-of-type(2) {
          transform-origin: top;
          transform: rotatez(-45deg);
        }
        input[type='checkbox']:checked ~ span:nth-of-type(3) {
          transform-origin: bottom;
          width: 48%;
          transform: translate(14px, -4px) rotatez(45deg);
        }
      }
      .menu-xs {
        height: calc(100vh - 4rem);
      }
    `,
  ],
  template: `
    <div class="fixed top-0 z-30 w-full" #root>
      <div class="container">
        <div
          class="relative z-30 flex items-center justify-between py-3 md:py-5 xl:py-6"
          >
          <a [routerLink]="localizedUrl.link('home')">
            <img
              class="hidden 2xl:block"
              src="/assets/img/logo.svg"
              alt="location-salles.ch"
              title="location-salles.ch"
              />
            <img
              class="w-10 2xl:hidden"
              src="/assets/img/icon-secondary.svg"
              alt="location-salles.ch"
              title="location-salles.ch"
              />
          </a>
          <!-- Toggler -->
          <div class="toggler xl:hidden">
            <label for="toggler" class="flex w-[32px] cursor-pointer flex-col">
              <input
                type="checkbox"
                id="toggler"
                class="hidden"
                [(ngModel)]="open"
                (ngModelChange)="menuToggled($event)"
                />
              <span
                class="bg-secondary-900 mx-[3px] h-[4px] w-1/2 rounded-[10px]"
              ></span>
              <span
                class="bg-secondary-900 mx-[3px] mt-[5px] h-[4px] w-full rounded-[10px]"
              ></span>
              <span
                class="bg-secondary-900 mx-[3px] mt-[5px] h-[4px] w-3/4 rounded-[10px]"
              ></span>
            </label>
          </div>
          @if ((appSection$ | async) === AppSection.PUBLIC) {
            <app-public-navbar-links
              class="hidden xl:flex xl:items-center xl:gap-4"
            ></app-public-navbar-links>
          }
        </div>
      </div>
      @if (open) {
        <div class="relative">
          <div
            class="menu-xs slide-in-right fixed top-16 h-screen w-full overflow-y-scroll border-t bg-neutral-100 xl:hidden"
            >
            @if ((appSection$ | async) === AppSection.MANAGER) {
              <app-sidebar-links
              ></app-sidebar-links>
            }
            @if ((appSection$ | async) === AppSection.PUBLIC) {
              <app-public-navbar-links
                class="my-12 flex flex-col items-start space-y-5 px-10"
              ></app-public-navbar-links>
            }
          </div>
        </div>
      }
    </div>
    `,
  standalone: false,
})
export class NavbarComponent extends DestroyableComponent implements OnInit {
  platformId = inject(PLATFORM_ID);
  protected localizedUrl = inject(LocalizedUrlService);
  @ViewChild('root') rootElement: ElementRef;
  open: boolean;
  appSection$: Observable<AppSection> = this.store.select(selectAppSection);
  AppSection = AppSection;
  user$ = this.store.select(selectAuthUser);
  isPublicNonB2C$ = this.store.select(fromStore.selectPublicAppSection).pipe(
    map(section => section === PublicAppSection.B2B)
  );
  isLoggedIn$ = this.store.select(fromStore.isLoggedIn);

  constructor(
    private router: Router,
    private store: Store,
  ) {
    super();

    afterNextRender(() => {
      document.addEventListener('scroll', () => {
        if (window.scrollY > 0) {
          this.rootElement.nativeElement.classList.add('bg-white');
        } else {
          this.rootElement.nativeElement.classList.remove('bg-white');
        }
      });
    });
  }

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationSkipped))
      .subscribe(() => {
        if (this.open) {
          this.open = false;
          this.menuToggled(false);
        }
      });

    this.user$
      .pipe(distinctUntilChanged((a, b) => a?.vendor?.id === b?.vendor?.id))
      .subscribe((user) => {
        this.open = false;
        this.menuToggled(false);
      });
  }

  menuToggled(isOpen: boolean) {
    if (isPlatformBrowser(this.platformId)) {
      if (isOpen) {
        document
          .querySelector('body')
          .classList.add('h-screen', 'overflow-hidden');
      } else {
        document
          .querySelector('body')
          .classList.remove('h-screen', 'overflow-hidden');
      }
    }
  }
}
